// src/lib/constants/media.ts

import type { VideoSortKey, SortDirection, DensityMode, VideoFilters } from '@/lib/types/Media';

/**
 * Media state types (matches backend MediaStateType enum values)
 */
export const MEDIA_STATE_TYPES = ['active', 'inactive'] as const;
export type MediaStateTypeValue = typeof MEDIA_STATE_TYPES[number];

/**
 * Video field constraints (used in Zod schema and UI character counters)
 */
export const VIDEO_CONSTRAINTS = {
  TITLE_MIN: 1,
  TITLE_MAX: 150,
  DESC_SHORT_MAX: 250,
  DESC_LONG_MAX: 4000,
  REF_ID_MAX: 150,
  NOTES_MAX: 4000,
  RELATED_LINK_TEXT_MAX: 100,
  RELATED_LINK_URL_MAX: 250,
} as const;

/**
 * Valid characters for reference ID
 */
export const REF_ID_PATTERN = /^[a-zA-Z0-9\-_]*$/;

/**
 * Text track types
 */
export const TEXT_TRACK_TYPES = ['captions', 'subtitles'] as const;

/**
 * Text track formats
 */
export const TEXT_TRACK_FORMATS = ['vtt'] as const;

/**
 * Accepted file extensions for text track uploads
 */
export const TEXT_TRACK_ACCEPTED_EXTENSIONS = ['.vtt'] as const;

/**
 * Query keys for TanStack Query - media feature
 */
export const MEDIA_QUERY_KEYS = {
  all: ['media'] as const,
  videos: (cId: number) => [...MEDIA_QUERY_KEYS.all, 'videos', cId] as const,
  video: (cId: number, mediaId: number) => [...MEDIA_QUERY_KEYS.videos(cId), mediaId] as const,
  streams: (cId: number) => [...MEDIA_QUERY_KEYS.all, 'streams', cId] as const,
  streamLiveStatus: (cId: number) => [...MEDIA_QUERY_KEYS.all, 'streamLiveStatus', cId] as const,
  // Sub-account consolidated data is scoped to the LOGIN account server-side; key by login cId
  subAccountStreams: (loginCId: number) => [...MEDIA_QUERY_KEYS.all, 'subAccountStreams', loginCId] as const,
  subAccountStreamLiveStatus: (loginCId: number) => [...MEDIA_QUERY_KEYS.all, 'subAccountStreamLiveStatus', loginCId] as const,
  stream: (cId: number, mediaId: number) => [...MEDIA_QUERY_KEYS.streams(cId), mediaId] as const,
  playlists: (cId: number) => [...MEDIA_QUERY_KEYS.all, 'playlists', cId] as const,
} as const;

/**
 * Grid column definitions for video table
 * Width values must match CSS classes in MediaGrid.module.css
 */
export const VIDEO_GRID_COLUMNS = [
  { key: 'checkbox', label: '', width: 48, sortable: false, align: 'center' as const },
  { key: 'thumbnail', label: '', width: 136, sortable: false, align: 'left' as const },
  { key: 'title', label: 'Title', width: 'auto', sortable: true, align: 'left' as const },
  { key: 'status', label: 'Status', width: 100, sortable: true, align: 'center' as const },
  { key: 'published', label: 'Published', width: 150, sortable: true, align: 'right' as const },
  { key: 'updated', label: 'Updated', width: 150, sortable: true, align: 'right' as const },
  { key: 'actions', label: '', width: 60, sortable: false, align: 'center' as const },
] as const;

export type VideoGridColumn = typeof VIDEO_GRID_COLUMNS[number];

/**
 * Default sort configuration (applies when no persisted preference exists)
 */
export const DEFAULT_SORT_KEY: VideoSortKey = 'published';
export const DEFAULT_SORT_DIRECTION: SortDirection = 'desc';

/**
 * Sort keys for iteration-heavy paths (precomputed grid orders, validation).
 */
export const VIDEO_SORT_KEYS = ['title', 'status', 'published', 'updated'] as const;

/**
 * Row heights for density modes (in pixels)
 */
export const ROW_HEIGHT: Record<DensityMode, number> = {
  comfortable: 84,  // Fits 68px thumbnail + padding, and two-row title
  compact: 56,
};

/**
 * Thumbnail dimensions (16:9 aspect ratio)
 */
export const THUMBNAIL_SIZE: Record<DensityMode, { width: number; height: number }> = {
  comfortable: { width: 120, height: 68 },
  compact: { width: 80, height: 45 },
};

/**
 * Virtualizer overscan (rows rendered outside viewport)
 */
export const VIRTUALIZER_OVERSCAN = 10;

/**
 * Runtime containment guard for fixed-height virtual list rows.
 */
export const VIDEO_GRID_MAX_VIRTUAL_ROWS = 72;

/**
 * Absolute viewport clamp for grid body to prevent runaway virtual window sizes
 * when an ancestor layout accidentally resolves to an unbounded height.
 */
export const VIDEO_GRID_MAX_VIEWPORT_HEIGHT_PX = 2200;

/**
 * Skip eager detail prefetch for very large datasets to avoid flooding the
 * network/cache during heavy interactions (sort/scroll/reorder).
 */
export const VIDEO_DETAIL_PREFETCH_MAX_ROWS = 1000;

/**
 * Inactive advanced-search filter state for the videos grid.
 * Shared reference is safe: filter arrays are replaced, never mutated.
 */
export const EMPTY_VIDEO_FILTERS: VideoFilters = {
  tagsAll: [],
  tagsAny: [],
  tagsNone: [],
  publishedAfter: null,
  publishedBefore: null,
  durationMin: null,
  durationMax: null,
  state: null,
  needsAttention: false,
};

/**
 * Statuses surfaced by the "Needs attention" filter — anything not ready
 * (stuck or failed ingest/processing). Deliberately excludes 'deleted'.
 */
export const NEEDS_ATTENTION_STATUSES = [
  'awaiting_ingest',
  'ingesting',
  'processing',
  'error',
] as const;

/**
 * Duration filter preset chips, tuned to long-form content (seconds; null = open-ended).
 */
export const DURATION_FILTER_PRESETS = [
  { label: 'Under 30 min', min: null, max: 1800 },
  { label: '30–60 min', min: 1800, max: 3600 },
  { label: '1–2 hr', min: 3600, max: 7200 },
  { label: '2–3 hr', min: 7200, max: 10800 },
  { label: 'Over 3 hr', min: 10800, max: null },
] as const;

/**
 * Published-date quick presets — set the "on or after" bound.
 * days = null means start of the current year (YTD).
 */
export const PUBLISHED_FILTER_PRESETS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: 'YTD', days: null },
] as const;

/**
 * Accepted image MIME types for poster upload.
 * Backend accepts JPG, PNG, HEIC, WebP as input and may output JPG, WebP, or PNG.
 */
export const POSTER_ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

/**
 * Extension fallback for HEIC/HEIF — some browsers report empty MIME for these formats.
 * Used by upload handlers when file.type is missing or not in POSTER_ACCEPTED_MIME_TYPES.
 */
export const POSTER_ACCEPTED_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif',
] as const;

/** Max poster file size: 20MB */
export const POSTER_MAX_FILE_SIZE = 20 * 1024 * 1024;

/**
 * Default poster dimensions — tells backend what output size to generate.
 * NOT validation constraints on the uploaded image (any aspect ratio accepted).
 * Matches lib2 DefaultPosterWidthHeight/DefaultThumbWidthHeight.
 */
export const DEFAULT_POSTER_SIZE = { width: 1280, height: 720 } as const;
export const DEFAULT_THUMB_SIZE = { width: 192, height: 108 } as const;

/** Merge constraints */
export const MERGE_MIN_VIDEOS = 2;
export const MERGE_MAX_DURATION_SECONDS = 43200; // 12 hours
/**
 * Hard cap on options handed to the merge modal's add-video Select. Mantine
 * keeps the dropdown subtree mounted (keepMounted), so every option passed in
 * is reconciled on each modal render — the cap must be enforced BEFORE the
 * data reaches the component (a custom OptionsFilter bypasses Select's limit).
 */
export const MERGE_ADD_SEARCH_MAX_OPTIONS = 50;

/**
 * Safety timeout for poster processing detection (ms).
 * If isProcessing is true for longer than this with no job status change,
 * clear processing state to prevent permanent spinner.
 */
export const POSTER_PROCESSING_TIMEOUT_MS = 2 * 60 * 1000;
